/**
 * Money and number formatting, driven by the ORGANISATION.
 *
 * Two bugs this replaces, both of which were invisible to anyone reading a
 * figure in India and wrong for everyone else:
 *
 *  1. Currency was conflated with language. Every amount was prefixed with a
 *     hardcoded `₹` — 132 occurrences across 13 pages, in five different
 *     spellings including a `{'₹'}` escape — so the flexpack deployment's
 *     Dubai organisation displayed rupees for dirhams. Currency belongs to the
 *     organisation, not to the interface language.
 *
 *  2. `.toFixed()` does not group digits at all. `(1234567.5).toFixed(2)` is
 *     `"1234567.50"`, and a reader has to count characters to know whether that
 *     is twelve lakh or a hundred and twenty. Indian grouping is 2-2-3
 *     (`12,34,567.50`), not the 3-3-3 that `en-US` would give, so even adding
 *     grouping with the wrong locale misreads the number.
 *
 * Intl.NumberFormat does both correctly and is in every browser we support.
 *
 * What is deliberately NOT routed through here: GPS coordinates
 * (`toFixed(4)` on a latitude must not be grouped), file sizes in KB,
 * durations in minutes, and percentages. Those were counted in the same
 * `.toFixed()` census and are not money — grouping a latitude would be a new
 * bug, not a fix. (Percentages have their own formatter, formatPercent, at
 * the bottom of this file — one decimal, no grouping, no locale.)
 */
import { display } from './dateDisplay';

import { useAuthStore } from '@/stores/authStore';

/** The organisation's formatting, as /auth/me reports it. */
export interface OrgFormat {
  /** ISO 4217, or null when the organisation has not configured one. */
  currency: string | null;
  /** BCP 47. Decides digit grouping: en-IN is 2-2-3, most others 3-3-3. */
  locale: string;
}

/**
 * Used before /auth/me has answered, and for the signed-out screens.
 *
 * `currency: null` on purpose. A default of INR is exactly the bug — it is
 * what made every Dubai figure a rupee figure — and a wrong currency reads as
 * authoritative in a way a missing one does not. `en-GB` groups 3-3-3, which
 * is the safe assumption when we know nothing: it is merely conventional,
 * whereas assuming Indian grouping asserts something about the customer.
 */
export const DEFAULT_ORG_FORMAT: OrgFormat = { currency: null, locale: 'en-GB' };

/**
 * The signed-in organisation's formatting, read straight from the auth store.
 *
 * Not a hook, on purpose. Money appears at 139 sites across 13 pages, inside
 * Recharts `tickFormatter` callbacks, table `cell` renderers and toast strings
 * — none of which is a component body, so a hook cannot be called there. This
 * reads fresh state on every call, and because every one of those call sites
 * runs during a render it is as current as a hook would be.
 *
 * The one thing it does NOT do is re-render on change. An organisation's
 * currency changes about never, and when it does the admin who changed it
 * reloads. `useOrgFormat()` exists for anywhere that genuinely needs to react.
 */
export function orgFormat(): OrgFormat {
  return orgFormatFrom(useAuthStore.getState().user);
}

/** Read whatever /auth/me gave us, tolerating an older response shape. */
export function orgFormatFrom(
  user: { org_currency?: string | null; org_locale?: string | null } | null | undefined,
): OrgFormat {
  return {
    currency: user?.org_currency ?? null,
    locale: user?.org_locale || DEFAULT_ORG_FORMAT.locale,
  };
}

const EM_DASH = '—';

/** Intl formatters are expensive to construct and are reused per call shape. */
const cache = new Map<string, Intl.NumberFormat>();
function formatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = locale + JSON.stringify(options);
  let f = cache.get(key);
  if (!f) {
    try {
      f = new Intl.NumberFormat(locale, options);
    } catch {
      // An unknown locale or currency code must not blank out a whole page of
      // figures. Fall back to the neutral locale and, if the currency itself
      // was the problem, to no currency at all.
      f = new Intl.NumberFormat(DEFAULT_ORG_FORMAT.locale, { ...options, style: 'decimal', currency: undefined });
    }
    cache.set(key, f);
  }
  return f;
}

export interface MoneyOptions {
  /** Decimals. Defaults to 0 — this app shows whole rupees/dirhams almost everywhere. */
  decimals?: number;
  /** What to render for null/undefined/NaN. Defaults to an em dash. */
  empty?: string;
}

/**
 * An amount, in the organisation's currency and grouping.
 *
 * With no configured currency the number is still grouped but carries no
 * symbol: the amount is real and worth showing, the currency is genuinely
 * unknown, and inventing one is the bug being fixed. Mirrors org_rate() in the
 * backend, which returns None rather than a rate nobody chose.
 */
export function formatMoney(
  value: number | null | undefined,
  org: OrgFormat,
  { decimals = 0, empty = EM_DASH }: MoneyOptions = {},
): string {
  if (value == null || Number.isNaN(value)) return empty;
  const base: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };
  if (!org.currency) return formatter(org.locale, base).format(value);
  return formatter(org.locale, {
    ...base,
    style: 'currency',
    currency: org.currency,
    currencyDisplay: 'narrowSymbol',
  }).format(value);
}

/**
 * A plain number with the organisation's grouping — counts, areas, kWh.
 *
 * Separate from formatMoney because these carry no currency and must not gain
 * one, but they DO want 12,34,567 rather than 1234567.
 */
export function formatNumber(
  value: number | null | undefined,
  org: OrgFormat,
  { decimals = 0, maxDecimals, empty = EM_DASH }: MoneyOptions & {
    /** UP TO this many decimals, trailing zeros dropped (6.68, 7, 7.5) —
     *  for a measured reading. Overrides `decimals`. */
    maxDecimals?: number;
  } = {},
): string {
  if (value == null || Number.isNaN(value)) return empty;
  return formatter(org.locale, maxDecimals != null
    ? { minimumFractionDigits: 0, maximumFractionDigits: maxDecimals }
    : { minimumFractionDigits: decimals, maximumFractionDigits: decimals },
  ).format(value);
}

/**
 * The currency symbol on its own, for a column header or an axis label —
 * "Cost (₹)". Empty string when no currency is configured, so a caller that
 * interpolates it produces "Cost" rather than "Cost ()".
 */
export function currencySymbol(org: OrgFormat): string {
  if (!org.currency) return '';
  try {
    const parts = new Intl.NumberFormat(org.locale, {
      style: 'currency', currency: org.currency, currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? org.currency;
  } catch {
    return org.currency;
  }
}

/** The one unit area is recorded in: every area column is square feet
 *  (leases.total_area_sqft, floors.total_area_sqft). */
export const AREA_UNIT = 'sq ft';

export interface QuantityOptions {
  /** "34.5K" / "3.5L" rather than "34,500" — for a headline card only. */
  compact?: boolean;
  /** Decimals for the full form. Defaults to 0. */
  decimals?: number;
  /** What to render for null/undefined/NaN. Defaults to an em dash. */
  empty?: string;
}

/**
 * A NON-MONEY figure with its unit: "34,500 sq ft", "12 desks", "8 days".
 * The organisation's digit grouping, never a currency symbol.
 *
 * 🔴 THE TOTAL AREA CARD READ "₹34.5K" (Bug_63). An area went through the
 * compact MONEY formatter, so square feet were printed as rupees. Quantities
 * have their own formatter now, and a guard test refuses a money formatter on
 * a figure labelled with a non-money unit.
 */
export function formatQuantity(
  value: number | null | undefined,
  unit: string,
  { compact = false, decimals = 0, empty = EM_DASH }: QuantityOptions = {},
): string {
  if (value == null || Number.isNaN(Number(value))) return empty;
  const org = orgFormat();
  const n = compact
    ? formatter(org.locale, { notation: 'compact', maximumFractionDigits: 1 } as Intl.NumberFormatOptions)
        .format(Number(value))
    : formatNumber(Number(value), org, { decimals });
  return unit ? `${n} ${unit}` : n;
}

/** An area, in the one unit areas are recorded in: "34,500 sq ft". */
export function formatArea(value: number | null | undefined, opts: QuantityOptions = {}): string {
  return formatQuantity(value, AREA_UNIT, opts);
}

/**
 * A form label naming the organisation's currency — "Monthly rent (₹)",
 * "Your target rent (₹/mo)" — never a code typed into the markup. With no
 * currency configured: the label alone, or "(per mo)" when a rate.
 *
 * 🔴 "Monthly rent (INR)" sat on the Add Lease form after every figure on the
 * page had moved to the organisation's currency (Bug_62 sweep), and a dozen
 * more labels said "(INR)" across six screens.
 */
export function moneyLabel(label: string, per?: string): string {
  const sym = currencySymbol(orgFormat());
  if (sym) return per ? `${label} (${sym}/${per})` : `${label} (${sym})`;
  return per ? `${label} (per ${per})` : label;
}

/** A compact axis/tick label: 12.3L, 4.5Cr for en-IN; 1.2M elsewhere. */
export function formatCompact(
  value: number | null | undefined,
  org: OrgFormat,
  { empty = EM_DASH }: { empty?: string } = {},
): string {
  if (value == null || Number.isNaN(value)) return empty;
  const sym = currencySymbol(org);
  const n = formatter(org.locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  } as Intl.NumberFormatOptions).format(value);
  return sym ? `${sym}${n}` : n;
}

/**
 * A billing period, from its two ends.
 *
 * 🔴 The Period column read `billing_month`, a single field the API has never
 * sent. The period is a RANGE — billing_period_start and billing_period_end,
 * both NOT NULL and populated on every live bill — so renaming the accessor
 * alone would have shown one date and called it the period.
 *
 * Collapses to one date when start and end are the same day, because a one-day
 * billing period is a real thing and "1 Jan – 1 Jan" reads as a mistake.
 * Returns the em-dash when either end is missing rather than half a range: a
 * period with one end is not a period.
 */
export function formatBillingPeriod(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start || !end) return EM_DASH;
  // The UTC-midnight shift this used to guard against by hand is handled by
  // dateDisplay, which reads a bare YYYY-MM-DD as the calendar date it is.
  const fmt = (iso: string) => display(iso.slice(0, 10), 'dateLong', { fallback: iso });
  const a = fmt(start);
  const b = fmt(end);
  return a === b ? a : `${a} – ${b}`;
}

/**
 * A percentage for display: ALWAYS one decimal — "9.8%", "10.0%".
 *
 * 🔴 "OCCUPANCY RATE SHOWS 10% FOR 5 OF 51 BAYS." 5 ÷ 51 is 9.8%, and the
 * page rounded it to a whole number. Every screen had picked its own rounding —
 * `Math.round`, `toFixed(0)`, `toFixed(1)`, or the raw float — so the same
 * kind of figure read differently from page to page, and a small number (0.4%
 * of desks in use) rounded to a confident 0%. One decimal everywhere: enough
 * to tell 9.8 from 10, and the same on every screen.
 *
 * For MEASURED or COMPUTED percentages only — occupancy, compliance, progress,
 * a change against last month. A CONFIGURED rate someone typed (GST 18%,
 * escalation 7.25%, a 9% discount rate) is shown exactly as entered: rounding
 * a money rate to one decimal would misstate it.
 *
 * `signed: true` prefixes "+" on a positive value, for changes. Zero carries
 * no sign, and -0 is normalised so "-0.0%" never appears.
 */
export function formatPercent(
  value: number | null | undefined,
  { signed = false }: { signed?: boolean } = {},
): string {
  if (value == null || !Number.isFinite(Number(value))) return EM_DASH;
  let v = Math.round(Number(value) * 10) / 10;
  if (Object.is(v, -0)) v = 0;
  return `${signed && v > 0 ? '+' : ''}${v.toFixed(1)}%`;
}

/**
 * part ÷ whole × 100, or null when there is no whole to divide by — for the
 * callers that hold counts rather than a ready-made percentage. Null, not 0:
 * "0% of nothing" asserts something the data does not say.
 */
export function percentOf(
  part: number | null | undefined,
  whole: number | null | undefined,
): number | null {
  if (part == null || whole == null || !whole) return null;
  if (!Number.isFinite(part) || !Number.isFinite(whole)) return null;
  return (part / whole) * 100;
}
